import React from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { Slide, Typography } from '@material-ui/core';
import liveChatBannerStyles from './AlmeLiveChatBanner.styles';
import { LiveChatNotificationResponseNext } from '@alme/types/response/LiveChatNotificationResponse';
import { AlmeLiveChatBannerConfig } from './AlmeLiveChatBannerConfig';

export type AlmeLiveChatBannerClasses = Record<"mainBanner" | "subBanner", string>;

interface AlmeLiveChatBannerProps extends WithStyles<typeof liveChatBannerStyles> {
    response: LiveChatNotificationResponseNext;
    responseId?: string | number;
    almeConfig: AlmeLiveChatBannerConfig;
    onScroll: () => void;
}

export function isValidLiveChatBannerResponse(response: LiveChatNotificationResponseNext): boolean {
    const hasOperatorJoined = response.extensionData.MessageOrigin === "Operator-Joined";
    const hasOperatorLeft = response.extensionData.MessageOrigin === "Operator-Left";
    const hasChatEnded = response.extensionData.MessageOrigin === "Session-End";
    return hasOperatorJoined || hasOperatorLeft || hasChatEnded;
}

const AlmeLiveChatBanner: React.FC<AlmeLiveChatBannerProps> = (props: AlmeLiveChatBannerProps) => {

    const response = props.response;
    const hasOperatorJoined = response.extensionData.MessageOrigin === "Operator-Joined";
    const hasOperatorLeft = response.extensionData.MessageOrigin === "Operator-Left";
    const hasChatEnded = response.extensionData.MessageOrigin === "Session-End";

    if (!isValidLiveChatBannerResponse(props.response)) {
        return null;
    }


    const responseRevision = (props.responseId || response.responseRevision).toString();
    const repName = response.extensionData.OperatorData?.Name || props.almeConfig.liveChatBanner.defaultRepName;
    const joinedSubText = `${props.almeConfig.liveChatBanner.joined.sub.text}${repName}`;
    const leftText = `${repName}${props.almeConfig.liveChatBanner.left.text}`;
    const sessionEndReason = response.extensionData.SessionEndReason || props.almeConfig.liveChatBanner.ended.reason.defaultReason;
    const endReasonText = `${sessionEndReason}${props.almeConfig.liveChatBanner.ended.reason.text}`;

    return (
        <>
            { hasOperatorJoined &&
                <Slide direction='up' in onEntered={props.onScroll}>
                    <div className={props.classes.root}>
                        <div className={props.classes.mainBanner} id={`alme-live-chat-banner-joined-main-${responseRevision}`}>
                            <Typography variant={props.almeConfig.liveChatBanner.joined.main.variant || 'h5'}>{props.almeConfig.liveChatBanner.joined.main.text}</Typography>
                        </div>
                        <div className={props.classes.subBanner} id={`alme-live-chat-banner-joined-sub-${responseRevision}`}>
                            <Typography variant={props.almeConfig.liveChatBanner.joined.sub.variant || 'subtitle2'}>{joinedSubText}</Typography>
                        </div>
                    </div>
                </Slide>
            }
            { hasOperatorLeft &&
                <Slide direction='up' in onEntered={props.onScroll}>
                    <div className={props.classes.root}>
                        <div className={props.classes.subBanner} id={`alme-live-chat-banner-left-${responseRevision}`}>
                            <Typography variant={props.almeConfig.liveChatBanner.left.variant || 'subtitle2'}>{leftText}</Typography>
                        </div>
                    </div>
                </Slide>
            }
            { hasChatEnded &&
                <Slide direction='up' in onEntered={props.onScroll}>
                    <div className={props.classes.root}>
                        <div className={props.classes.mainBanner} id={`alme-live-chat-banner-end-main-${responseRevision}`}>
                            <Typography variant={props.almeConfig.liveChatBanner.ended.main.variant || 'h5'}>{props.almeConfig.liveChatBanner.ended.main.text}</Typography>
                        </div>
                        <div className={props.classes.subBanner} id={`alme-live-chat-banner-end-sub-${responseRevision}`}>
                            <Typography variant={props.almeConfig.liveChatBanner.ended.sub.variant || 'subtitle1'}>{props.almeConfig.liveChatBanner.ended.sub.text}</Typography>
                        </div>
                        <div className={props.classes.subBanner} id={`alme-live-chat-banner-end-reason-${responseRevision}`}>
                            <Typography variant={props.almeConfig.liveChatBanner.ended.reason.variant || 'subtitle2'}>{endReasonText}</Typography>
                        </div>
                    </div>
                </Slide>
            }
        </>
    );
};

export default withStyles(liveChatBannerStyles)(AlmeLiveChatBanner);